/**
 * HW Variant Selector — shared JS driver for V1 (bulk size) and V2 (full matrix).
 * Ported and adapted from harveys-theme/assets/bulk-size-selector.js.
 * Uses data-hw-* attributes to avoid conflicts with the theme's native variant picker.
 */

let hwCartInputsStates = new Map();
var hwGlobalInitialized = false;

document.addEventListener("DOMContentLoaded", () => {
  initializeHwVariantSelector();
  hwSetupQuickViewObserver();
});

function hwGetContainerKey(container) {
  if (container === document) return "main";
  const tag = container.tagName.toLowerCase();
  if (tag === "quick-view-modal" || tag === "quick-add-drawer")
    return "quick-view";
  if (container.closest("[data-hw-variant-selector]")) return "v2";
  return "unknown";
}

function hwGetCartState(container) {
  const key = hwGetContainerKey(container);
  if (!hwCartInputsStates.has(key)) {
    hwCartInputsStates.set(key, []);
  }
  return hwCartInputsStates.get(key);
}

function hwSetCartState(container, state) {
  hwCartInputsStates.set(hwGetContainerKey(container), state);
}

function hwResetCartState(container) {
  hwCartInputsStates.set(hwGetContainerKey(container), []);
}

function hwSetupQuickViewObserver() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (
            node.tagName === "QUICK-VIEW-MODAL" ||
            node.tagName === "QUICK-ADD-DRAWER" ||
            node.querySelector("quick-view-modal") ||
            node.querySelector("quick-add-drawer")
          ) {
            setTimeout(() => hwInitializeQuickView(), 200);
          }
          const quickView = hwFindOpenQuickViewModal();
          if (
            quickView &&
            quickView.contains(node) &&
            (node.matches("[data-hw-size-quantity-input]") ||
              node.querySelector("[data-hw-size-quantity-input]"))
          ) {
            setTimeout(() => hwInitializeQuickView(), 100);
          }
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

document.addEventListener("quick-view:loaded", () => hwInitializeQuickView());
document.addEventListener("drawer:open", () => {
  setTimeout(() => {
    if (hwFindOpenQuickViewModal()) hwInitializeQuickView();
  }, 300);
});
document.addEventListener("on:quickbuy:before-open", (evt) => {
  const drawer = evt.target.closest("quick-add-drawer");
  if (drawer) {
    // Clear the initialized flag before new content is loaded,
    // so the MutationObserver will re-initialize when it detects
    // the fresh [data-hw-size-quantity-input] nodes.
    delete drawer.dataset.hwInitialized;
  }
});

function hwFindOpenQuickViewModal() {
  // Check quick-view-modal elements
  let qv = document.querySelector("quick-view-modal[open]:not([hidden])");
  if (qv) return qv;
  qv = document.querySelector("quick-view-modal[active]:not([hidden])");
  if (qv) return qv;
  qv = document.querySelector('quick-view-modal[open=""]');
  if (qv) return qv;
  qv = document.querySelector('quick-view-modal[active=""]');
  if (qv) return qv;
  const allModals = document.querySelectorAll("quick-view-modal");
  for (const modal of allModals) {
    if (modal.offsetParent !== null && !modal.hasAttribute("hidden"))
      return modal;
  }
  // Check quick-add-drawer (used by enterprise theme)
  const qaDrawer = document.querySelector(
    'quick-add-drawer[aria-hidden="false"]',
  );
  if (qaDrawer) return qaDrawer;
  return null;
}

function hwInitializeQuickView() {
  const container = hwFindOpenQuickViewModal();
  if (!container) return;
  const selectors = [
    "[data-hw-size-quantity-input]",
    "input[data-hw-size-value]",
  ];
  let inputs = [];
  for (const sel of selectors) {
    inputs = container.querySelectorAll(sel);
    if (inputs.length > 0) break;
  }
  const productData = container.querySelector(
    "script[data-hw-product-variants]",
  );
  if (inputs.length === 0 || !productData) return;
  initializeHwVariantSelector(container);
}

function initializeHwVariantSelector(container) {
  if (
    container &&
    container !== document &&
    container.dataset &&
    container.dataset.hwInitialized === "true"
  )
    return;
  if (container === document || !container) {
    if (hwGlobalInitialized) return;
    hwGlobalInitialized = true;

    // The customisation app submits through its own AJAX path and announces
    // success with hca:cart:add instead of the theme's on:cart:add. Reset the
    // main page quantities once so sizes don't linger after a customised add.
    document.addEventListener("hca:cart:add", hwResetHwInputs);
  }
  if (!container) container = document;

  // Try multiple selectors for cart forms (enterprise theme uses js-product-form, harveys-theme uses data-type)
  let cartForms = container.querySelectorAll(
    'form[data-type="add-to-cart-form"], form.js-product-form',
  );
  if (cartForms.length === 0)
    cartForms = container.querySelectorAll('form[id^="product-form-"]');

  let qtyInputs = container.querySelectorAll("[data-hw-size-quantity-input]");
  if (qtyInputs.length === 0)
    qtyInputs = container.querySelectorAll("input[data-hw-size-value]");

  if (qtyInputs.length === 0) return;

  if (container.dataset) container.dataset.hwInitialized = "true";

  cartForms.forEach((form) => {
    hwOverrideForm(form);
  });

  // Hide the native single-variant quantity input — HW selector has its own
  container
    .querySelectorAll(
      ".js-product-form-main quantity-input, .js-product-form-main [data-quantity-input]",
    )
    .forEach(function (el) {
      el.style.display = "none";
    });

  container
    .querySelectorAll(".product-info__dynamic-checkout-button")
    .forEach(function (el) {
      el.style.display = "none";
    });

  qtyInputs.forEach((input) => {
    hwBindQuantityEvents(input, container);
    hwBindStockEvent(input, container);
  });

  // Sync data-hw-max to the native max attribute so stepUp() respects it.
  container
    .querySelectorAll("[data-hw-size-quantity-input]")
    .forEach((input) => {
      const max = input.getAttribute("data-hw-max");
      if (max !== null) input.setAttribute("max", max);
    });

  // Bind plus button with long-press support.
  // Uses the input's native stepUp() method which fires change events
  // without interfering with focus — same pattern as the original theme.
  container.querySelectorAll("[data-hw-qty-plus]").forEach((btn) => {
    const getInput = () => {
      const qtyWrapper =
        btn.closest(".hw-quantity-input") || btn.closest(".hw-quantity");
      return qtyWrapper
        ? qtyWrapper.querySelector("[data-hw-size-quantity-input]")
        : null;
    };

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const input = getInput();
      if (input) {
        input.stepUp();
        input.dispatchEvent(new CustomEvent("step", { bubbles: true }));
        hwUpdateStockIndicator(input, container);
      }
    });
  });

  // Bind minus button — uses native stepDown().
  container.querySelectorAll("[data-hw-qty-minus]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const qtyWrapper =
        btn.closest(".hw-quantity-input") || btn.closest(".hw-quantity");
      if (!qtyWrapper) return;
      const input = qtyWrapper.querySelector("[data-hw-size-quantity-input]");
      if (input) {
        input.stepDown();
        input.dispatchEvent(new CustomEvent("step", { bubbles: true }));
        hwUpdateStockIndicator(input, container);
      }
    });
  });

  function hwResetHwInputs() {
    // Reset all HW quantity inputs to 0
    container
      .querySelectorAll("[data-hw-size-quantity-input]")
      .forEach(function (inp) {
        inp.value = 0;
        hwUpdateMinusButton(inp);
      });
    hwResetCartState(container);
    hwUpdateCartForm(container);
    hwUpdateAddButtonState(container);
  }

  document.addEventListener("on:cart:add", hwResetHwInputs);
  document.addEventListener("on:line-item:change", hwResetHwInputs);
  document.addEventListener("clearCartState", hwResetHwInputs);

  // Optimistic reset: when any add-to-cart button is clicked, reset immediately
  container.addEventListener("click", function (e) {
    var addBtn = e.target.closest('[name="add"], [data-hw-v2-add-to-cart]');
    if (addBtn) {
      // Small delay to let the form submission start, then reset
      setTimeout(hwResetHwInputs, 100);
    }
  });

  hwDisableUnavailableVariants(container);

  // Run on every variant/option change — the variant picker may disable the button
  function hwRefreshBtn() {
    setTimeout(function () {
      // Recompute unavailable sizes so the greyed-out state follows the
      // currently selected colour (the initial computation happens before
      // any option is selected).
      hwDisableUnavailableVariants(container);
      // Re-resolve the cart state with the current selection so the hidden
      // items[] inputs never hold stale variant ids (e.g. the fallback
      // colour resolved before a colour was chosen). Also refreshes the
      // Add to Cart button state.
      hwUpdateCartState(container);
    }, 50);
  }
  document.addEventListener("variant:changed", hwRefreshBtn);
  // The picker announces completed variant changes with this bubbling event
  container.addEventListener("on:variant:change", hwRefreshBtn);
  var nativePicker = container.querySelector("variant-picker");
  if (nativePicker) nativePicker.addEventListener("change", hwRefreshBtn);
  container.addEventListener("change", function (e) {
    if (e.target.matches(".opt-btn, .custom-select__option")) hwRefreshBtn();
  });

  // V2 modal "Add to Cart" button — submits the product form and closes the modal
  container
    .querySelectorAll("[data-hw-v2-add-to-cart]")
    .forEach(function (btn) {
      btn.addEventListener("click", function () {
        var form = document.querySelector(".js-product-form-main");
        if (!form) form = document.querySelector('form[id^="product-form-"]');
        if (!form) return;

        // Ensure hidden items[] inputs are current
        hwUpdateCartForm(container);

        // Guard: don't submit if no HW quantities are set
        var hasQty = false;
        container
          .querySelectorAll("[data-hw-size-quantity-input]")
          .forEach(function (inp) {
            if (parseInt(inp.value, 10) > 0) hasQty = true;
          });
        if (!hasQty) return;

        // Close the modal
        var productId = btn.getAttribute("data-hw-v2-add-to-cart");
        if (productId) {
          var modal = document.getElementById("hw-v2-matrix-" + productId);
          if (modal && modal.close) modal.close();
        }

        // Click the native submit button so product-form.js handles the AJAX submission
        var submitBtn = form.querySelector('[name="add"]');
        if (submitBtn && !submitBtn.disabled) {
          submitBtn.click();
        } else {
          // Fallback: requestSubmit on the form directly
          form.requestSubmit();
        }
      });
    });

  // V2 matrix row click handling
  container.querySelectorAll("[data-hw-matrix-row]").forEach((row) => {
    row.addEventListener("click", (e) => {
      if (e.target.closest("[data-hw-size-quantity-input]")) return; // let quantity input handle its own clicks
      const variantId = row.dataset.hwMatrixRow;
      const cells = container.querySelectorAll(
        `[data-hw-variant-id="${variantId}"]`,
      );
      cells.forEach((cell) => cell.classList.toggle("hw-matrix-row--selected"));
    });
  });
}

function hwUsesV2VariantIds(container) {
  var inputs = container.querySelectorAll("[data-hw-size-quantity-input]");
  for (var i = 0; i < inputs.length; i++) {
    if (inputs[i].dataset.hwVariantId) return true;
  }
  return false;
}

function hwIsSelectionComplete(container) {
  var selectedOptions = hwGetSelectedOptions(container);
  var cartForm = container.querySelector(
    'form.js-product-form, form[id^="product-form-"], form[data-type="add-to-cart-form"]',
  );
  if (!cartForm) return true; // can't determine — don't block the add

  var variants = [];
  try {
    variants = JSON.parse(cartForm.dataset.hwVariants || "[]");
  } catch (e) {}

  if (variants.length === 0) return true; // can't determine — don't block

  var sizeIndex = hwGetSizeOptionIndex(variants[0], container);
  if (sizeIndex === -1) return true; // no size option — nothing to gate

  var optionCount = (variants[0].options || []).length;
  for (var i = 0; i < optionCount; i++) {
    if (i === sizeIndex) continue;
    if (!selectedOptions[i]) return false;
  }
  return true;
}

function hwGetPurchaseBlock(container) {
  const info = container && container.closest ? container.closest(".product-info") : null;
  return (
    (info ? info.querySelector("[data-hw-purchase-options]") : null) ||
    document.querySelector("[data-hw-purchase-options]")
  );
}

function hwIsSubscriptionActive(container) {
  const block = hwGetPurchaseBlock(container);
  if (!block) return false;
  const card = block.querySelector('[data-hw-option="subscribe"]');
  return !!(card && card.classList.contains("is-selected"));
}

function hwGetSubscriptionMinQty(container) {
  const block = hwGetPurchaseBlock(container);
  const min = block ? parseInt(block.dataset.minSubscribeQty, 10) : NaN;
  return Number.isFinite(min) && min > 0 ? min : 10;
}

function hwGetSubscriptionPlanId(container) {
  const block = hwGetPurchaseBlock(container);
  return block && block.dataset.selectedPlan ? block.dataset.selectedPlan : null;
}

function hwGetTotalQuantity(container) {
  let total = 0;
  container.querySelectorAll("[data-hw-size-quantity-input]").forEach((input) => {
    total += parseInt(input.value, 10) || 0;
  });
  return total;
}

function hwUpdateSubscriptionMessage(container) {
  const block = hwGetPurchaseBlock(container);
  const msg = block ? block.querySelector(".hw-subscribe-min-msg") : null;
  if (!msg) return;
  const active = hwIsSubscriptionActive(container);
  const belowMin =
    active && hwGetTotalQuantity(container) < hwGetSubscriptionMinQty(container);
  msg.hidden = !belowMin;
}

function hwUpdateAddButtonState(container) {
  var btn = container.querySelector('[name="add"]');
  if (!btn) return;

  var subscriptionActive = hwIsSubscriptionActive(container);
  var min = hwGetSubscriptionMinQty(container);
  var total = hwGetTotalQuantity(container);
  var selectionComplete = hwUsesV2VariantIds(container) || hwIsSelectionComplete(container);

  // Subscription: the total across all sizes must meet the minimum quantity.
  // Give the button a clear next-action message that updates as each
  // requirement is met.
  if (subscriptionActive && total < min) {
    var remaining = min - total;
    btn.disabled = true;
    btn.setAttribute("aria-disabled", "true");
    btn.textContent = selectionComplete
      ? "Add " + remaining + " more cases to continue"
      : "Select a colour and add " + remaining + " more cases to continue";
    hwUpdateSubscriptionMessage(container);
    return;
  }

  hwUpdateSubscriptionMessage(container);

  // Check if any HW quantity input has a value > 0
  var hwInputs = container.querySelectorAll("[data-hw-size-quantity-input]");
  var hasHwQty = false;
  hwInputs.forEach(function (inp) {
    if (parseInt(inp.value, 10) > 0) hasHwQty = true;
  });

  if (!hasHwQty) {
    // No size/colour quantities entered — the form would submit no items (and
    // Shopify rejects it), so prompt for a selection instead of enabling.
    btn.disabled = true;
    btn.setAttribute("aria-disabled", "true");
    btn.textContent = selectionComplete
      ? "Select a size to continue"
      : "Select a size and colour to continue";
    return;
  }

  // V2 matrix inputs carry explicit variant ids, so they're safe to enable.
  // V1 requires every non-size option (e.g. colour) to be selected — a
  // partial selection would resolve sizes to the first available variant
  // and put the wrong item in the cart.
  if (selectionComplete) {
    btn.disabled = false;
    btn.removeAttribute("aria-disabled");
    if (btn.dataset.addToCartText) btn.textContent = btn.dataset.addToCartText;
  } else {
    btn.disabled = true;
    btn.setAttribute("aria-disabled", "true");
    btn.textContent = subscriptionActive
      ? "Select a colour to continue"
      : btn.dataset.preselectionText || "Select options";
  }
}

function hwOverrideForm(cartForm) {
  if (cartForm.dataset.hwOverrideForm) return;
  cartForm.dataset.hwOverrideForm = "true";

  // Bypass HTML5 validation on the hidden required id input
  cartForm.noValidate = true;

  // Remove the standard single-variant id/quantity inputs so they don't conflict
  // with the per-size items[] lines the bulk selector writes.
  var neutralizeIdAndQuantity = function () {
    cartForm
      .querySelectorAll('input[name="id"], input[name="quantity"]')
      .forEach(function (el) {
        el.name = "";
      });
  };
  neutralizeIdAndQuantity();

  // The native variant-picker re-adds name="id" asynchronously after a colour
  // change (it copies the freshly-fetched input's attributes back onto the live
  // node). Watch for that and re-neutralise it so the bulk selector's items[]
  // lines stay the single source of truth for the selected size.
  var idInputObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (
        mutation.type !== "attributes" ||
        mutation.attributeName !== "name"
      )
        return;
      var el = mutation.target;
      if (
        el instanceof HTMLInputElement &&
        (el.name === "id" || el.name === "quantity")
      ) {
        el.name = "";
      }
    });
  });
  idInputObserver.observe(cartForm, {
    subtree: true,
    attributes: true,
    attributeFilter: ["name"],
  });

  // Load variant data for ID resolution
  var productData =
    cartForm.closest("[data-hw-variant-selector]") ||
    document.querySelector("[data-hw-product-variants]");
  if (productData) {
    try {
      cartForm.dataset.hwVariants =
        productData.textContent || productData.innerText;
    } catch (e) {}
  }
}

function hwUpdateCartForm(container) {
  var cartForms = container.querySelectorAll(
    'form[data-type="add-to-cart-form"], form.js-product-form, form[id^="product-form-"]',
  );
  var cartState = hwGetCartState(container);

  cartForms.forEach(function (cartForm) {
    // Remove existing hidden items[] inputs
    cartForm
      .querySelectorAll('input[name^="items["]')
      .forEach(function (input) {
        input.remove();
      });

    // Neutralise the app widget's top-level selling_plan input — the plan is
    // carried on each items[] line (only when the selector is in play).
    cartForm
      .querySelectorAll('input[name="selling_plan"]')
      .forEach(function (input) {
        input.name = "";
      });

    // Add new hidden inputs for each item in the cart state
    cartState.forEach(function (item, index) {
      if (!item.variantId) return;

      var idInput = document.createElement("input");
      idInput.type = "hidden";
      idInput.name = "items[" + index + "][id]";
      idInput.value = item.variantId;
      cartForm.appendChild(idInput);

      var qtyInput = document.createElement("input");
      qtyInput.type = "hidden";
      qtyInput.name = "items[" + index + "][quantity]";
      qtyInput.value = item.quantity;
      cartForm.appendChild(qtyInput);

      // Subscriptions: carry the selected selling plan on each line item. The
      // plan id comes from our purchase-options block (selectedPlan), not the
      // app widget's top-level selling_plan input.
      if (hwIsSubscriptionActive(container)) {
        var sellingPlan = hwGetSubscriptionPlanId(container);
        if (sellingPlan) {
          var spInput = document.createElement("input");
          spInput.type = "hidden";
          spInput.name = "items[" + index + "][selling_plan]";
          spInput.value = sellingPlan;
          cartForm.appendChild(spInput);
        }
      }
    });
  });
}

function hwResolveVariantId(sizeValue, container) {
  // V2: variant ID stored directly on the input
  // V1: resolve from size value + selected options
  var cartForm = container.querySelector(
    'form.js-product-form, form[id^="product-form-"], form[data-type="add-to-cart-form"]',
  );
  if (!cartForm) return null;

  var variants = [];
  try {
    variants = JSON.parse(cartForm.dataset.hwVariants || "[]");
  } catch (e) {}

  if (variants.length === 0) return null;

  var sizeIndex = hwGetSizeOptionIndex(variants[0], container);
  if (sizeIndex === -1) return null;

  var selectedOptions = hwGetSelectedOptions(container);

  // Strict resolution: only resolve a variant when every non-size option is
  // selected and matches. Never fall back to the first available variant —
  // a partial selection (e.g. no colour chosen yet) must not silently add a
  // different variant than the customer intends.
  for (var v = 0; v < variants.length; v++) {
    var opts = variants[v].options || [];
    if (opts[sizeIndex] !== sizeValue) continue;
    var match = true;
    for (var i = 0; i < selectedOptions.length; i++) {
      if (i === sizeIndex) continue;
      if (!selectedOptions[i] || opts[i] !== selectedOptions[i]) {
        match = false;
        break;
      }
    }
    if (match) return variants[v].id;
  }
  return null;
}

function hwBindQuantityEvents(input, container) {
  const update = () => {
    hwUpdateCartState(container);
    hwUpdateMinusButton(input);
  };
  input.addEventListener("step", update);
  // Initial state
  hwUpdateMinusButton(input);
}

function hwUpdateMinusButton(input) {
  const qtyWrapper =
    input.closest(".hw-quantity-input") || input.closest(".hw-quantity");
  if (!qtyWrapper) return;
  const minusBtn = qtyWrapper.querySelector("[data-hw-qty-minus]");
  if (!minusBtn) return;
  const val = parseInt(input.value, 10) || 0;
  minusBtn.disabled = val <= 0;
}

function hwBindStockEvent(input, container) {
  // Stock validation on blur
  input.addEventListener("blur", () => {
    const max = parseInt(input.getAttribute("data-hw-max"), 10);
    if (!isNaN(max) && parseInt(input.value, 10) > max) {
      input.value = max;
    }
    if (parseInt(input.value, 10) < 0) input.value = 0;
  });

  // Stock indicator update on focus — shows stock when user clicks a size row
  input.addEventListener("focus", () => {
    hwUpdateStockIndicator(input, container);
  });

  // Stock indicator update on input — fires on every +/- click and typed change
  input.addEventListener("input", () => {
    hwUpdateStockIndicator(input, container);
  });

  // Stock indicator update on change — mirrors harveys-theme bindQuantityStockEvent
  input.addEventListener("change", () => {
    hwUpdateStockIndicator(input, container);
  });
}

function hwUpdateStockIndicator(input, container) {
  var stockEl = container.querySelector('[id^="hw-size-stock-"]');
  if (!stockEl) return;

  var sizeValue = input.dataset.hwSizeValue;
  if (!sizeValue) {
    stockEl.innerHTML = "";
    stockEl.className = "hw-stock-status";
    return;
  }

  // Resolve the exact variant ID for this size + currently selected options
  var variantId = hwResolveVariantId(sizeValue, container);

  // Get all variants data (needed for fallback and inventory tracking check)
  var variantsEl =
    container.querySelector("[data-hw-product-variants]") ||
    document.querySelector("[data-hw-product-variants]");
  if (!variantsEl) return;

  var variants = [];
  try {
    variants = JSON.parse(variantsEl.textContent || variantsEl.innerText);
  } catch (e) {
    return;
  }
  if (variants.length === 0) return;

  // With an incomplete selection (e.g. no colour chosen) the strict resolver
  // returns nothing for every size. Don't mislabel that as out of stock —
  // clear the indicator until the full selection exists.
  var selectedOptions = hwGetSelectedOptions(container);
  var stockSizeIndex = hwGetSizeOptionIndex(variants[0], container);
  for (var si = 0; si < selectedOptions.length; si++) {
    if (si === stockSizeIndex) continue;
    if (!selectedOptions[si]) {
      stockEl.innerHTML = "";
      stockEl.className = "hw-stock-status";
      return;
    }
  }

  // If no exact variant matches (size not available in this colour), show out of stock
  var variant = null;
  if (variantId) {
    for (var vi = 0; vi < variants.length; vi++) {
      if (String(variants[vi].id) === String(variantId)) {
        variant = variants[vi];
        break;
      }
    }
  }

  // No variant at all for this size → out of stock
  if (!variant) {
    stockEl.className = "hw-stock-status hw-stock-status--out";
    stockEl.innerHTML = "Out of stock";
    return;
  }

  // Variant exists but inventory isn't tracked → nothing to show
  if (variant.inventory_management === null) {
    stockEl.innerHTML = "";
    stockEl.className = "hw-stock-status";
    return;
  }

  // Get inventory data
  var invEl =
    container.querySelector("[data-hw-product-inventory]") ||
    document.querySelector("[data-hw-product-inventory]");
  if (!invEl) return;

  var invData = [];
  try {
    invData = JSON.parse(invEl.textContent || invEl.innerText);
  } catch (e) {
    return;
  }

  var inv = null;
  for (var i = 0; i < invData.length; i++) {
    if (invData[i].id === variant.id) {
      inv = invData[i];
      break;
    }
  }
  if (!inv) {
    stockEl.className = "hw-stock-status hw-stock-status--out";
    stockEl.innerHTML = "Out of stock";
    return;
  }

  var available = inv.inventory_quantity;
  var isContinueSelling = inv.inventory_policy === "continue";

  // Subtract cart state (use == for number/string comparison)
  var cartState = hwGetCartState(container);
  for (var c = 0; c < cartState.length; c++) {
    if (cartState[c].variantId == variant.id) {
      available -= cartState[c].quantity;
      break;
    }
  }

  // Back-order message when over stock on a continue-selling variant
  if (isContinueSelling && available <= 0) {
    stockEl.className = "hw-stock-status hw-stock-status--low";
    stockEl.innerHTML =
      "This item is on back order and may take an additional 2 - 3 weeks to arrive";
    return;
  }

  // Disable + button and set max when stock exhausted (unless continue-selling)
  var qtyWrapper =
    input.closest(".hw-quantity-input") || input.closest(".hw-quantity");
  var plusBtn = qtyWrapper
    ? qtyWrapper.querySelector("[data-hw-qty-plus]")
    : null;
  if (plusBtn) {
    if (available <= 0 && !isContinueSelling) {
      plusBtn.disabled = true;
      if (parseInt(input.value, 10) > inv.inventory_quantity) {
        input.value = inv.inventory_quantity;
      }
    } else {
      plusBtn.disabled = false;
    }
  }

  // Set colour class + text based on stock level
  // Continue-selling variants: always green "In stock" unless over stock (handled above)
  if (isContinueSelling) {
    stockEl.className = "hw-stock-status hw-stock-status--in-stock";
    stockEl.innerHTML = "In stock";
  } else if (available <= 0) {
    stockEl.className = "hw-stock-status hw-stock-status--out";
    stockEl.innerHTML = "Out of stock";
  } else if (available <= 5) {
    stockEl.className = "hw-stock-status hw-stock-status--out";
    stockEl.innerHTML = "Low stock";
  } else if (available <= 10) {
    stockEl.className = "hw-stock-status hw-stock-status--low";
    stockEl.innerHTML = "Low stock";
  } else {
    stockEl.className = "hw-stock-status hw-stock-status--in-stock";
    stockEl.innerHTML = "In stock";
  }
}

function hwUpdateCartState(container) {
  const inputs = container.querySelectorAll("[data-hw-size-quantity-input]");
  const state = [];
  inputs.forEach((input) => {
    const qty = parseInt(input.value, 10);
    if (qty <= 0) return;

    let variantId = input.dataset.hwVariantId;

    // V1: resolve variant ID from size value
    if (!variantId && input.dataset.hwSizeValue) {
      variantId = hwResolveVariantId(input.dataset.hwSizeValue, container);
    }

    if (variantId) {
      state.push({
        variantId: variantId,
        quantity: qty,
        value: input.dataset.hwSizeValue || input.dataset.hwVariantId,
      });
    }
  });
  hwSetCartState(container, state);
  hwUpdateCartForm(container);
  hwUpdateAddButtonState(container);
}

function hwDisableUnavailableVariants(container) {
  // Get currently selected options from the native variant picker
  const selectedOptions = hwGetSelectedOptions(container);

  // Get all variant data
  const productData =
    container.querySelector("[data-hw-product-variants]") ||
    document.querySelector("[data-hw-product-variants]");
  if (!productData) return;

  let variants = [];
  try {
    variants = JSON.parse(productData.textContent || productData.innerText);
  } catch (e) {
    return;
  }

  // For each quantity input, check if its variant is available with current selections
  container
    .querySelectorAll("[data-hw-size-quantity-input]")
    .forEach((input) => {
      const sizeValue = input.dataset.hwSizeValue || input.dataset.hwVariantId;
      const wrapper =
        input.closest("[data-hw-size-option]") || input.parentElement;
      const matchingVariants = variants.filter((v) => {
        // For V1: match by size option value and selected other options
        // For V2: match by variant ID
        if (input.dataset.hwVariantId) {
          return String(v.id) === input.dataset.hwVariantId;
        }
        // V1: check all options match
        if (!sizeValue) return false;
        const opts = v.options || [];
        const sizeOptionIndex = hwGetSizeOptionIndex(variants[0], container);
        if (sizeOptionIndex === -1) return false;
        if (opts[sizeOptionIndex] !== sizeValue) return false;
        // Check other options match selected options
        for (let i = 0; i < selectedOptions.length; i++) {
          if (i === sizeOptionIndex) continue;
          if (selectedOptions[i] && opts[i] !== selectedOptions[i])
            return false;
        }
        return true;
      });

      const isAvailable = matchingVariants.some((v) => v.available);
      if (wrapper) {
        wrapper.classList.toggle("hw-unavailable", !isAvailable);
        const qtyWrapper =
          wrapper.closest("quantity-input") ||
          wrapper.querySelector("quantity-input");
        if (qtyWrapper)
          qtyWrapper.classList.toggle("hw-disabled", !isAvailable);
      }
      if (!isAvailable && parseInt(input.value, 10) > 0) {
        // The current selection can't buy this size — zero it so a stale
        // quantity is never submitted (mirrors the old selector).
        input.value = 0;
        hwUpdateMinusButton(input);
      }
      if (input.dataset.hwMax === undefined && matchingVariants.length > 0) {
        input.dataset.hwMax = matchingVariants[0].inventory_quantity;
      }
    });
}

function hwGetSelectedOptions(container) {
  var selected = [];
  var picker = container.querySelector("variant-picker");
  if (picker) {
    // Get selected options from the native variant picker
    picker.querySelectorAll(".option-selector").forEach(function (selector) {
      var checked = selector.querySelector(".opt-btn:checked");
      if (checked) {
        selected.push(checked.value);
      } else {
        // Dropdown pickers hold their value in a native select element
        var nativeSelect = selector.querySelector(".custom-select__native");
        if (nativeSelect && nativeSelect.value) {
          selected.push(nativeSelect.value);
        } else {
          // A single-valued option (e.g. aprons with one 107cm length) is
          // inherently resolved — there's nothing to choose.
          var btns = selector.querySelectorAll(".opt-btn");
          if (btns.length === 1) {
            selected.push(btns[0].value);
          } else {
            selected.push(null);
          }
        }
      }
    });
  }

  // Keep nulls when nothing is selected (e.g. data-select-first-variant is
  // false). Availability checks treat null as "any value", so every colour is
  // considered a possibility. The old fallback to variants[0].options pinned
  // availability to the first variant's colour and greyed out sizes that are
  // in stock in other colours.
  return selected;
}

function hwGetSizeOptionIndex(referenceVariant, container) {
  if (!referenceVariant || !referenceVariant.options) return -1;
  // The rendered grid knows the actual size option index (e.g. aprons grids
  // colours when Length is single-valued), so trust it over heuristics.
  const scopedRoot = container && container !== document ? container : document;
  const grid = scopedRoot.querySelector("[data-hw-size-index]") || document.querySelector("[data-hw-size-index]");
  if (grid) {
    const idx = parseInt(grid.dataset.hwSizeIndex, 10);
    if (!isNaN(idx) && idx >= 0) return idx;
  }
  // The options JSON mirrors what the picker renders. Scope to the current
  // container so a featured-product/quick-view script can't hijack the main
  // product's option list (and vice versa).
  const optionsScript =
    scopedRoot.querySelector("[data-hw-product-options]") ||
    document.querySelector("[data-hw-product-options]");
  let options = [];
  try {
    if (optionsScript)
      options = JSON.parse(optionsScript.textContent || optionsScript.innerText);
  } catch (e) {}

  if (options.length) {
    // Prefer the "Size" option.
    let sizeIdx = -1;
    for (let i = 0; i < options.length; i++) {
      if (options[i].name.toLowerCase() === "size") { sizeIdx = i; break; }
    }
    if (sizeIdx === -1) {
      for (let i = 0; i < options.length; i++) {
        if (options[i].name.toLowerCase() === "length") { sizeIdx = i; break; }
      }
    }

    // On subscription products where the size/length option is single-valued,
    // the bulk grid is rendered per colour instead — return that option.
    if (sizeIdx !== -1 && options[sizeIdx].values && options[sizeIdx].values.length <= 1) {
      const purchaseBlock =
        scopedRoot.querySelector("[data-hw-purchase-options]") ||
        document.querySelector("[data-hw-purchase-options]");
      if (purchaseBlock) {
        for (let i = 0; i < options.length; i++) {
          if (options[i].name.toLowerCase() === "colour" && options[i].values && options[i].values.length > 1) {
            return i;
          }
        }
      }
    }

    if (sizeIdx !== -1) return sizeIdx;
  }

  // Fallback: assume size is the last option
  return referenceVariant.options.length - 1;
}

// (Re-initialization is handled by the MutationObserver for dynamic content)

// Public API for other scripts
window.hwVariantSelector = {
  initialize: initializeHwVariantSelector,
  refresh: function () {
    hwGlobalInitialized = false;
    initializeHwVariantSelector();
  },
};
